
import { supabase } from '@/lib/supabase';
import { LineItemMarkupTier } from '@/types/line-item-markup-tiers';

export interface PricingCalculationResult {
  markup: number;
  margin: number;
  finalPrice: number;
  sourceName: 'custom' | 'category' | 'global';
}

export const calculateCurrentPricing = async (
  amount: number,
  categoryId?: string,
  companyId?: string,
  customTiers: LineItemMarkupTier[] = [],
  useCustomTiers: boolean = false,
  customMarkup?: number
): Promise<PricingCalculationResult> => {
  // Priority 1: Custom markup tiers (if using tiers)
  if (useCustomTiers && customTiers.length > 0) {
    const applicableTier = customTiers.find(tier => 
      amount >= tier.min_amount && 
      (tier.max_amount === null || amount < tier.max_amount)
    );
    
    if (applicableTier) {
      return {
        markup: applicableTier.markup_percentage,
        margin: applicableTier.margin_percentage,
        finalPrice: amount * (1 + applicableTier.markup_percentage / 100),
        sourceName: 'custom' as const
      };
    }
  }
  
  // Priority 2: Custom markup percentage (simple override, not tiered)
  if (customMarkup !== undefined && customMarkup !== null) {
    const margin = parseFloat(((customMarkup / (100 + customMarkup)) * 100).toFixed(2));
    return {
      markup: customMarkup,
      margin,
      finalPrice: amount * (1 + customMarkup / 100),
      sourceName: 'custom' as const
    };
  }
  
  if (categoryId && companyId) {
    try {
      const { data: markupData } = await supabase
        .rpc('get_category_markup', { 
          p_category_id: categoryId,
          p_amount: amount,
          p_company_id: companyId
        });
        
      const { data: marginData } = await supabase
        .rpc('get_category_margin', { 
          p_category_id: categoryId,
          p_amount: amount,
          p_company_id: companyId
        });
      
      if (markupData !== null) {
        return {
          markup: markupData,
          margin: marginData || 0,
          finalPrice: amount * (1 + markupData / 100),
          sourceName: 'category' as const
        };
      }
    } catch (error) {
      console.error('Error fetching category pricing:', error);
    }
  }
  
  // Global fallback
  try {
    const { data: settings } = await supabase
      .from('cost_center_settings')
      .select('default_markup, default_margin')
      .eq('company_id', companyId!)
      .maybeSingle();
      
    const markup = settings?.default_markup || 0;
    const margin = settings?.default_margin || 0;
    
    return {
      markup,
      margin,
      finalPrice: amount * (1 + markup / 100),
      sourceName: 'global' as const
    };
  } catch (error) {
    console.error('Error fetching global settings:', error);
    return {
      markup: 0,
      margin: 0,
      finalPrice: amount,
      sourceName: 'global' as const
    };
  }
};
